import { Component, OnInit, ViewChild } from "@angular/core";
import { ErrorHandlerDialogComponent } from "../../error-handler-dialog/error-handler-dialog.component";
import { PropulsionRolloutService } from "../../../services/propulsion-rollout.service";
import { MatDialog } from "@angular/material/dialog";
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  Validators,
} from "@angular/forms";
import { ExcelExportService } from "../../../services/excel-export.service";
import { MatPaginator } from "@angular/material/paginator";
import { ReportConstants } from "../ReportConstants";
import { ReaderService } from "../../../services/reader.service";
import * as _ from "lodash";

interface Menu {
  model: UntypedFormControl;
  availableOptions: any[];
  selectedOptions: any[] | string;
}

@Component({
    selector: "app-propulsion-rollout",
    templateUrl: "./propulsion-rollout.component.html",
    styleUrls: ["./propulsion-rollout.component.scss"],
    standalone: false
})
export class PropulsionRolloutComponent implements OnInit {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  propulsionList: any = [];
  columnList: any[] = [];
  isLoading = false;
  displayTable = false;
  dataSource: any;
  totalSize = 0;
  constants: ReportConstants = new ReportConstants();
  defaultBrand: any = [];

  year: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: null,
  };

  propulsion: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  oem: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  brand: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  architecture: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  bodyStyle: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  segment: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  status: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  region: Menu = {
    model: new FormControl("", Validators.required),
    availableOptions: [],
    selectedOptions: [],
  };

  formGroup = new FormGroup({
    year: this.year.model,
    propulsion: this.propulsion.model,
    oem: this.oem.model,
    brand: this.brand.model,
    architecture: this.architecture.model,
    bodyStyle: this.bodyStyle.model,
    segment: this.segment.model,
    status: this.status.model,
    region: this.region.model,
  });

  constructor(
    public readonly dialog: MatDialog,
    private readonly propulsionRolloutService: PropulsionRolloutService,
    private readonly excelExport: ExcelExportService,
    private readonly readerService: ReaderService
  ) {}

  ngOnInit() {
    this.beginLoading();
    this.propulsionRolloutService.getAPLReportMenuOptions().subscribe({
      next: (x: any) => {
        this.handleAPLMenuItems(x);
      },
      error: (err: any) => {
        this.endLoading();
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: err.error === null ? err.message : err.error.message,
          },
        });
      },
      complete: () => {
        this.selectAllMenuOptions();
        this.formGroup.updateValueAndValidity();
        this.endLoading();
        this.subscribeToFormControls();
      },
    });
  }

  public subscribeToFormControls(): void {
    this.formGroup.controls["year"].valueChanges.subscribe((value) => {
      this.onChangeYear(value);
    });
    this.formGroup.controls["propulsion"].valueChanges.subscribe((value) => {
      this.onChangePropulsion(value);
    });
    this.formGroup.controls["oem"].valueChanges.subscribe((value) => {
      this.onChangeOem(value);
    });
    this.formGroup.controls["brand"].valueChanges.subscribe((value) => {
      this.onChangeBrand(value);
    });
    this.formGroup.controls["architecture"].valueChanges.subscribe((value) => {
      this.onChangeArchitecture(value);
    });
    this.formGroup.controls["bodyStyle"].valueChanges.subscribe((value) => {
      this.onChangeBodyStyle(value);
    });
    this.formGroup.controls["segment"].valueChanges.subscribe((value) => {
      this.onChangeSegment(value);
    });
    this.formGroup.controls["status"].valueChanges.subscribe((value) => {
      this.onChangeStatus(value);
    });
    this.formGroup.controls["region"].valueChanges.subscribe((value) => {
      this.onChangeRegion(value);
    });
  }

  public generateReport(): void {
    if (this.verifySearchCriteria()) {
      this.displayTable = false;
      this.beginLoading();
      this.propulsionRolloutService
        .getPropulsionReportData(
          this.propulsion.selectedOptions as any[],
          this.oem.selectedOptions as any[],
          this.brand.selectedOptions as any[],
          this.region.selectedOptions as any[],
          this.architecture.selectedOptions as any[],
          this.bodyStyle.selectedOptions as any[],
          this.segment.selectedOptions as any[],
          this.status.selectedOptions as any[],
          this.year.selectedOptions as string
        )
        .subscribe({
          next: (x: any) => {
            this.handlePropulsionReportData(x);
          },
          error: (err: any) => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: {
                message: err.error === null ? err.message : err.error.message,
              },
            });
            this.endLoading();
          },
          complete: () => {
            this.endLoading();
          },
        });
    } else {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: { message: "Please specify search criteria." },
      });
    }
  }

  public handlePropulsionReportData(data: any): void {
    if (data.displayDataList && data.displayDataList.length > 0) {
      this.displayTable = true;
      this.propulsionList = data.displayDataList;
      this.totalSize = data.displayDataList.length;
      this.columnList = data.columnNames;
      this.propulsionList.paginator = this.paginator;
    } else {
      this.clearReport();
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: {
          message:
            "There are no entries matching the selected report criteria.",
        },
      });
    }
  }

  public optionsToString(options: string[]): string {
    let ret: string;
    ret = options[0] + ",";
    if (options.length > 1) {
      for (let i = 1; i < options.length; i++) {
        ret += "," + options[i];
      }
    }
    return ret;
  }

  public beginLoading(): void {
    this.isLoading = true;
  }

  public endLoading(): void {
    this.isLoading = false;
  }

  public onChangeYear(newValue: string): void {
    this.year.selectedOptions = newValue;
  }

  public onChangePropulsion(newValue: string[]): void {
    this.propulsion.selectedOptions = newValue;
  }

  public onChangeOem(newValue: string[]): void {
    this.oem.selectedOptions = newValue;
    if (newValue != null && this.oem.selectedOptions.length > 0) {
      const oems = this.optionsToString(this.oem.selectedOptions as any[]);
      this.readerService.getBrandForOem(oems).subscribe({
        next: (res) => {
          this.brand.selectedOptions = _.filter(
            this.brand.selectedOptions,
            (x) => {
              return res.includes(x);
            }
          );
          this.formGroup.controls["brand"].setValue(
            this.brand.selectedOptions,
            { onlySelf: true, emitEvent: false }
          );
          this.brand.availableOptions = res;
        },
        error: (res) => {
          console.log(res);
        },
        complete: () => {},
      });
    }
  }

  public onChangeBrand(newValue: string[]): void {
    this.brand.selectedOptions = newValue;
  }

  public onChangeArchitecture(newValue: string[]): void {
    this.architecture.selectedOptions = newValue;
  }

  public onChangeBodyStyle(newValue: string[]): void {
    this.bodyStyle.selectedOptions = newValue;
  }

  public onChangeSegment(newValue: string[]): void {
    this.segment.selectedOptions = newValue;
  }

  public onChangeStatus(newValue: string[]): void {
    this.status.selectedOptions = newValue;
  }

  public onChangeRegion(newValue: string[]): void {
    this.region.selectedOptions = newValue;
  }

  public exportToExcel(): void {
    if (this.verifySearchCriteria()) {
      this.beginLoading();
      this.propulsionRolloutService
        .exportToExcel(
          this.propulsion.selectedOptions as any[],
          this.oem.selectedOptions as any[],
          this.brand.selectedOptions as any[],
          this.region.selectedOptions as any[],
          this.architecture.selectedOptions as any[],
          this.bodyStyle.selectedOptions as any[],
          this.segment.selectedOptions as any[],
          this.status.selectedOptions as any[],
          this.year.selectedOptions as string
        )
        .subscribe({
          next: (response: any) => {
            this.excelExport.exportToExcelFromServer(
              response,
              "PropulsionRolloutReport"
            );
          },
          error: () => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: { message: "Error occurred while creating excel file." },
            });
            this.endLoading();
          },
          complete: () => {
            this.endLoading();
          },
        });
    } else {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: { message: "Please specify search criteria." },
      });
    }
  }

  public clearFilters(): void {
    this.propulsion.selectedOptions = [];
    this.formGroup.controls["propulsion"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.oem.selectedOptions = [];
    this.formGroup.controls["oem"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.brand.selectedOptions = [];
    this.formGroup.controls["brand"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.architecture.selectedOptions = [];
    this.formGroup.controls["architecture"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.bodyStyle.selectedOptions = [];
    this.formGroup.controls["bodyStyle"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.segment.selectedOptions = [];
    this.formGroup.controls["segment"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.status.selectedOptions = [];
    this.formGroup.controls["status"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.region.selectedOptions = [];
    this.formGroup.controls["region"].setValue("", {
      onlySelf: true,
      emitEvent: true,
    });

    this.year.selectedOptions = null;
    this.formGroup.controls["year"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });

    this.brand.availableOptions = this.defaultBrand;
    this.formGroup.controls["brand"].setValue("", {
      onlySelf: true,
      emitEvent: false,
    });
    this.formGroup.markAsUntouched();
    this.formGroup.reset();
  }

  public handleAPLMenuItems(menuItems: any): void {
    this.propulsion.availableOptions = menuItems.propulsionTypeItems;
    this.oem.availableOptions = menuItems.oemItems;
    this.brand.availableOptions = [];
    this.brand.availableOptions = menuItems.brandItems;
    this.architecture.availableOptions = menuItems.architectureItems;
    this.bodyStyle.availableOptions = menuItems.bodyStyleItems;
    this.segment.availableOptions = menuItems.segmentItems;
    this.status.availableOptions = menuItems.statusItems;
    this.region.availableOptions = menuItems.regionItems;
    this.year.availableOptions = menuItems.yearsToDisplay;
    this.defaultBrand = menuItems.brandItems;
  }

  public verifySearchCriteria(): boolean {
    return this.formGroup.valid;
  }

  public clearReport(): void {
    this.displayTable = false;
    this.columnList = [];
  }

  public selectAllMenuOptions(): void {
    const date = new Date();
    this.year.selectedOptions = String(date.getFullYear());
    this.formGroup.controls["year"].markAsTouched();
    this.formGroup.controls["year"].setValue(this.year.selectedOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.propulsion.selectedOptions = this.propulsion.availableOptions;
    this.formGroup.controls["propulsion"].setValue(
      this.propulsion.availableOptions,
      { onlySelf: true, emitEvent: false }
    );

    this.oem.selectedOptions = this.oem.availableOptions;
    this.formGroup.controls["oem"].setValue(this.oem.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.brand.availableOptions = this.defaultBrand;
    this.brand.selectedOptions = this.brand.availableOptions;
    this.formGroup.controls["brand"].setValue(this.brand.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.architecture.selectedOptions = this.architecture.availableOptions;
    this.formGroup.controls["architecture"].setValue(
      this.architecture.availableOptions,
      { onlySelf: true, emitEvent: false }
    );

    this.bodyStyle.selectedOptions = this.bodyStyle.availableOptions;
    this.formGroup.controls["bodyStyle"].setValue(
      this.bodyStyle.availableOptions,
      { onlySelf: true, emitEvent: false }
    );

    this.segment.selectedOptions = this.segment.availableOptions;
    this.formGroup.controls["segment"].setValue(this.segment.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.status.selectedOptions = this.status.availableOptions;
    this.formGroup.controls["status"].setValue(this.status.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });

    this.region.selectedOptions = this.region.availableOptions;
    this.formGroup.controls["region"].setValue(this.region.availableOptions, {
      onlySelf: true,
      emitEvent: false,
    });
  }

  public centerText(column): boolean {
    let result = false;
    if (!isNaN(column)) {
      result = true;
    }
    return result;
  }

  public colorBkgrnd(cell): boolean {
    let result = false;
    if (cell !== "") {
      result = true;
    }
    return result;
  }
}
